/**
 * Type definitions for the Manufacturing Warehouse System
 * Covers: suppliers, contractors, lots, BOMs, production cycles,
 * external processings, samples, labels, disposals, and unified movements.
 */

// ═══════════════════════════════════════════════════════════════
// Enums
// ═══════════════════════════════════════════════════════════════

export type ProductClass = 'standard' | 'materia_prima' | 'semilavorato' | 'referenza';

export type LotStatus = 'available' | 'reserved' | 'in_production' | 'with_contractor' | 'exhausted' | 'disposed';

export type LotOriginType = 'ddt_inbound' | 'production' | 'external_processing' | 'manual' | 'sample_return';

export type DdtStatus = 'draft' | 'received' | 'loaded' | 'invoiced';

export type ProductionCycleStatus = 'draft' | 'in_progress' | 'completed' | 'cancelled';

export type ExternalProcessingStatus = 'draft' | 'sent' | 'in_progress' | 'returned' | 'completed' | 'cancelled';

export type SampleStatus = 'active' | 'in_analysis' | 'returned' | 'disposed' | 'archived';

export type WasteDestination = 'recovery' | 'resale' | 'disposal';

export type ExternalWasteDestination = 'sold_to_contractor' | 'sold_to_third_party' | 'disposal';

export type DisposalSource = 'production' | 'processing' | 'sample' | 'manual';

export type LabelFormat = 'small' | 'a5' | 'shipping_4x6' | 'custom';

export type PrinterOrientation = 'portrait' | 'landscape';

export type QrAccess = 'public' | 'authenticated';

export type ZoneType = 'storage' | 'production' | 'external_contractor' | 'sample' | 'quarantine';

export type MovementType =
  | 'load'
  | 'ddt_out'
  | 'production_in'
  | 'production_out'
  | 'contractor_out'
  | 'contractor_in'
  | 'zone_transfer'
  | 'sample_taken'
  | 'sample_returned'
  | 'disposal'
  | 'waste'
  | 'waste_sale'
  | 'label_print'
  | 'adjustment'
  | 'retransformation_in'
  | 'retransformation_out';

// ═══════════════════════════════════════════════════════════════
// Snapshot types
// ═══════════════════════════════════════════════════════════════

export interface SnapshotProductData {
  name: string;
  sku?: string;
  product_class?: ProductClass;
  unit?: string;
  [key: string]: unknown;
}

export interface SnapshotSupplierData {
  ragione_sociale: string;
  partita_iva?: string;
  email?: string;
  telefono?: string;
  [key: string]: unknown;
}

export interface SnapshotContractorData {
  ragione_sociale: string;
  partita_iva?: string;
  email?: string;
  telefono?: string;
  specialization?: string;
  [key: string]: unknown;
}

export interface SnapshotBomData {
  bom_id?: string;
  product_name: string;
  output_quantity: number;
  output_unit: string;
  lines: Array<{
    component_product_id: string;
    component_name: string;
    quantity_per_unit: number;
    unit: string;
  }>;
  [key: string]: unknown;
}

// ═══════════════════════════════════════════════════════════════
// Entities
// ═══════════════════════════════════════════════════════════════

// --- Supplier (Fornitore) ---

export interface WhSupplier {
  id: string;
  organization_id: string;
  codice?: string;
  ragione_sociale: string;
  tipo_soggetto?: string;
  partita_iva?: string;
  codice_fiscale?: string;
  indirizzo?: string;
  citta?: string;
  provincia?: string;
  cap?: string;
  paese: string;
  telefono?: string;
  telefono_2?: string;
  cellulare?: string;
  fax?: string;
  email?: string;
  pec?: string;
  codice_sdi?: string;
  payment_terms?: string;
  notes?: string;
  fatture_in_cloud_id?: number;
  default_warehouse_1_id?: string;
  default_warehouse_2_id?: string;
  default_causale?: string;
  default_destination?: string;
  iban?: string;
  is_active: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface WhSupplierFormData {
  codice?: string;
  ragione_sociale: string;
  tipo_soggetto?: string;
  partita_iva?: string;
  codice_fiscale?: string;
  indirizzo?: string;
  citta?: string;
  provincia?: string;
  cap?: string;
  paese?: string;
  telefono?: string;
  telefono_2?: string;
  cellulare?: string;
  fax?: string;
  email?: string;
  pec?: string;
  codice_sdi?: string;
  payment_terms?: string;
  notes?: string;
  fatture_in_cloud_id?: number;
  is_active?: boolean;
}

// --- Contractor (Terzista) ---

export interface WhContractor {
  id: string;
  organization_id: string;
  ragione_sociale: string;
  partita_iva?: string;
  codice_fiscale?: string;
  indirizzo?: string;
  citta?: string;
  provincia?: string;
  cap?: string;
  telefono?: string;
  email?: string;
  pec?: string;
  specialization?: string;
  notes?: string;
  is_active: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface WhContractorFormData {
  ragione_sociale: string;
  partita_iva?: string;
  codice_fiscale?: string;
  indirizzo?: string;
  citta?: string;
  provincia?: string;
  cap?: string;
  telefono?: string;
  email?: string;
  pec?: string;
  specialization?: string;
  notes?: string;
  is_active?: boolean;
}

// --- Lot (Lotto) ---

export interface ProductLot {
  id: string;
  organization_id: string;
  product_id: string;
  lot_number: string;
  zone_id?: string;
  quantity: number;
  initial_quantity: number;
  unit: string;
  status: LotStatus;
  origin_type?: LotOriginType;
  origin_id?: string;
  /** Data reale di produzione (20260910f). NULL sui lotti storici. */
  production_date?: string | null;
  expiration_date?: string;
  notes?: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by?: string;

  // Relations (populated by queries)
  product?: { id: string; name: string; sku?: string; product_class?: ProductClass };
  zone?: { id: string; name: string; code: string };
  slots?: WhLotSlot[];
}

export interface WhLotSlot {
  id: string;
  organization_id: string;
  lot_id: string;
  expiry_date?: string;
  quantity: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// --- Inbound DDT (Bolla in Entrata) ---

export interface WhInboundDdt {
  id: string;
  organization_id: string;
  supplier_id: string;
  ddt_number: string;
  ddt_date: string;
  registration_date?: string;
  status: DdtStatus;
  document_type_id?: string;
  causale?: string;
  reference?: string;
  destination?: string;
  notes?: string;
  total_amount?: number;
  archived?: boolean;
  evaso?: boolean;
  warehouse_1_id?: string;
  warehouse_2_id?: string;
  // Template di stampa DDT di riferimento (wh_ddt_print_templates) — determina
  // quali campi extra configurati mostrare nel form (vedi ddtExtraFields.ts)
  print_template_id?: string;
  snapshot_supplier_data: SnapshotSupplierData;
  fattura_in_cloud_invoice_id?: number;
  received_by?: string;
  received_at?: string;
  invoiced_by?: string;
  invoiced_at?: string;
  // Flag SALDATO (spec Ideariso D5): valorizzato = documento saldato al fornitore
  paid_at?: string;
  paid_by?: string;
  confirmed_production: boolean;
  confirmed_production_by?: string;
  confirmed_production_at?: string;
  confirmed_commercial: boolean;
  confirmed_commercial_by?: string;
  confirmed_commercial_at?: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by?: string;

  // Relations
  supplier?: WhSupplier;
  document_type?: WhDocumentType;
  lines?: WhInboundDdtLine[];
}

export interface WhInboundDdtLine {
  id: string;
  organization_id: string;
  ddt_id: string;
  product_id: string;
  quantity: number;
  unit: string;
  lot_number?: string;
  supplier_lot?: string;
  expiry_date?: string;
  unit_cost?: number;
  notes?: string;
  detail?: string;
  colli?: number;
  bancali_qty?: number;
  bancali_type?: string;
  unita_singola?: number;
  warehouse_zone_id?: string;
  supplier_order_line_id?: string;
  // Valori dei campi extra riga configurati per il template (vedi
  // ddtExtraFields.ts), chiave = DdtExtraFieldDef.key
  extra_fields?: Record<string, unknown>;
  snapshot_product_data: SnapshotProductData;
  loaded: boolean;
  loaded_lot_id?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;

  // Relations
  product?: { id: string; name: string; sku?: string };
}

export interface WhInboundDdtFormData {
  supplier_id: string;
  ddt_number: string;
  ddt_date: string;
  registration_date?: string;
  notes?: string;
  document_type_id?: string;
  warehouse_1_id?: string;
  warehouse_2_id?: string;
  causale?: string;
  reference?: string;
  destination?: string;
  fattura_in_cloud_invoice_id?: number;
  metadata?: Record<string, unknown>;
  print_template_id?: string | null;
  lines: WhInboundDdtLineFormData[];
}

export interface WhInboundDdtLineFormData {
  id?: string;
  product_id: string;
  quantity: number;
  unit?: string;
  lot_number?: string;
  supplier_lot?: string;
  expiry_date?: string;
  unit_cost?: number;
  notes?: string;
  detail?: string;
  colli?: number;
  bancali_qty?: number;
  bancali_type?: string;
  unita_singola?: number;
  warehouse_zone_id?: string;
  supplier_order_line_id?: string;
  extra_fields?: Record<string, unknown>;
}

// --- BOM (Distinta Base) ---

export interface WhBom {
  id: string;
  organization_id: string;
  product_id: string;
  name?: string;
  version: number;
  is_active: boolean;
  /** Tipo distinta (Ideariso 4b): 'production' default, una attiva per (prodotto, tipo) */
  bom_type?: string;
  notes?: string;
  output_quantity: number;
  output_unit: string;
  base_lavoro?: number;
  created_at: string;
  updated_at: string;
  created_by?: string;

  // Relations
  product?: { id: string; name: string; sku?: string; product_class?: ProductClass };
  lines?: WhBomLine[];
}

export interface WhBomLine {
  id: string;
  organization_id: string;
  bom_id: string;
  component_product_id: string;
  quantity_per_unit: number;
  unit: string;
  sort_order: number;
  notes?: string;
  phase?: string;
  material_type?: string;
  created_at: string;
  updated_at: string;

  // Relations
  component_product?: { id: string; name: string; sku?: string; product_class?: ProductClass; stock_quantity?: number };
}

/** Nodo albero esplosione (verso il basso, componenti) o implosione (verso l'alto, dove viene usato). */
export interface BomTreeNode {
  productId: string;
  productName: string;
  sku?: string;
  /** Quantità richiesta per 1 unità del nodo genitore (0 per la radice). */
  quantityPerUnit: number;
  /** Solo esplosione: quantità cumulata rispetto a 1 unità della radice. */
  cumulativeQuantity?: number;
  unit: string;
  level: number;
  bomName?: string;
  bomType?: string;
  /** Esplosione con piu' distinte tipizzate: nodo intestazione di una distinta, non un prodotto. */
  isGroup?: boolean;
  hasChildren: boolean;
  isCycle: boolean;
  isTruncated: boolean;
  children: BomTreeNode[];
}

export interface WhBomFormData {
  product_id: string;
  name?: string;
  bom_type?: string;
  output_quantity?: number;
  output_unit?: string;
  base_lavoro?: number;
  notes?: string;
  lines: WhBomLineFormData[];
}

export interface WhBomLineFormData {
  id?: string;
  component_product_id: string;
  quantity_per_unit: number;
  unit?: string;
  sort_order?: number;
  notes?: string;
  phase?: string;
  material_type?: string;
}

// --- Production Cycle (Ciclo di Produzione) ---

export interface WhProductionCycle {
  id: string;
  organization_id: string;
  output_product_id: string;
  output_quantity: number;
  output_unit: string;
  output_lot_id?: string;
  bom_id?: string;
  status: ProductionCycleStatus;
  transformation_type?: string;
  progressive_number?: number;
  transformation_date?: string;
  is_retransformation: boolean;
  source_lot_id?: string;
  snapshot_bom?: SnapshotBomData;
  snapshot_product_data: SnapshotProductData;
  started_at?: string;
  completed_at?: string;
  started_by?: string;
  completed_by?: string;
  notes?: string;
  archived?: boolean;
  output_expiry_date?: string;
  output_detail?: string;
  output_colli?: number;
  output_bancali_qty?: number;
  output_bancali_type?: string;
  output_unita_singola?: number;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by?: string;

  // Relations
  output_product?: { id: string; name: string; sku?: string };
  output_lot?: ProductLot;
  bom?: WhBom;
  inputs?: WhProductionCycleInput[];
  waste?: WhProductionCycleWaste[];
}

export interface WhProductionCycleInput {
  id: string;
  organization_id: string;
  cycle_id: string;
  product_id: string;
  lot_id?: string;
  quantity_used: number;
  waste_quantity?: number;
  unit: string;
  snapshot_product_data: SnapshotProductData;
  created_at: string;

  // Relations
  product?: { id: string; name: string; sku?: string };
  lot?: ProductLot;
}

export interface WhProductionCycleWaste {
  id: string;
  organization_id: string;
  cycle_id: string;
  waste_type: string;
  name?: string;
  quantity: number;
  unit: string;
  destination: WasteDestination;
  destination_details?: string;
  recovery_lot_id?: string;
  created_at: string;
}

export interface WhProductionCycleFormData {
  output_product_id: string;
  output_quantity: number;
  output_unit?: string;
  output_lot_number: string;
  output_existing_lot_id?: string;
  output_zone_id?: string;
  output_expiry_date?: string;
  output_detail?: string;
  output_colli?: number;
  output_bancali_qty?: number;
  output_bancali_type?: string;
  output_unita_singola?: number;
  bom_id?: string;
  transformation_type?: string;
  transformation_date?: string;
  is_retransformation?: boolean;
  source_lot_id?: string;
  notes?: string;
  inputs: WhProductionCycleInputFormData[];
  waste?: WhProductionCycleWasteFormData[];
}

export interface WhProductionCycleInputFormData {
  product_id: string;
  lot_id?: string;
  quantity_used: number;
  unit?: string;
}

export interface WhProductionCycleWasteFormData {
  waste_type: string;
  name?: string;
  quantity: number;
  unit?: string;
  destination: WasteDestination;
  destination_details?: string;
  /** Articolo scartato: assente = il prodotto ottenuto; un componente (es. etichette rovinate) si scala dal suo lotto */
  product_id?: string;
  lot_id?: string;
}

// --- External Processing (Lavorazione Esterna) ---

export interface WhExternalProcessing {
  id: string;
  organization_id: string;
  contractor_id: string;
  processing_type?: string;
  status: ExternalProcessingStatus;
  ddt_out_number?: string;
  ddt_out_date?: string;
  ddt_in_number?: string;
  ddt_in_date?: string;
  snapshot_contractor_data: SnapshotContractorData;
  fattura_in_cloud_ddt_out_id?: number;
  fattura_in_cloud_ddt_in_id?: number;
  sent_at?: string;
  returned_at?: string;
  notes?: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by?: string;

  // Relations
  contractor?: WhContractor;
  items?: WhExternalProcessingItem[];
  waste?: WhExternalProcessingWaste[];
}

export interface WhExternalProcessingItem {
  id: string;
  organization_id: string;
  processing_id: string;
  product_id: string;
  lot_out_id?: string;
  lot_in_id?: string;
  quantity_out: number;
  quantity_in?: number;
  unit: string;
  snapshot_product_data: SnapshotProductData;
  created_at: string;
  updated_at: string;

  // Relations
  product?: { id: string; name: string; sku?: string };
  lot_out?: ProductLot;
  lot_in?: ProductLot;
}

export interface WhExternalProcessingWaste {
  id: string;
  organization_id: string;
  processing_id: string;
  waste_type: string;
  name?: string;
  quantity: number;
  unit?: string;
  destination: ExternalWasteDestination;
  destination_details?: string;
  created_at: string;
}

export interface WhExternalProcessingFormData {
  contractor_id: string;
  processing_type?: string;
  ddt_out_number?: string;
  ddt_out_date?: string;
  notes?: string;
  items: WhExternalProcessingItemFormData[];
}

export interface WhExternalProcessingItemFormData {
  product_id: string;
  lot_out_id?: string;
  quantity_out: number;
  unit?: string;
}

export interface WhExternalProcessingReturnFormData {
  ddt_in_number?: string;
  ddt_in_date?: string;
  items: Array<{
    item_id: string;
    quantity_in: number;
    lot_in_number: string;
    lot_in_zone_id?: string;
  }>;
  waste?: Array<{
    waste_type: string;
    name?: string;
    quantity: number;
    unit?: string;
    destination: ExternalWasteDestination;
    destination_details?: string;
  }>;
}

// --- Lot Sample (Campione di Lotto) ---

export interface WhLotSample {
  id: string;
  organization_id: string;
  lot_id: string;
  quantity: number;
  unit: string;
  zone_id?: string;
  status: SampleStatus;
  reason?: string;
  returned_quantity?: number;
  returned_at?: string;
  disposed_at?: string;
  notes?: string;
  sampled_by?: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;

  // Relations
  lot?: ProductLot;
  zone?: { id: string; name: string; code: string };
}

export interface WhLotSampleFormData {
  lot_id: string;
  quantity: number;
  unit?: string;
  zone_id?: string;
  reason?: string;
  notes?: string;
}

// --- Label Template ---

export interface WhLabelFieldConfig {
  field: string;
  label?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
  textAlign?: 'left' | 'center' | 'right';
  visible: boolean;
}

export interface WhPrinterConfig {
  id: string;
  organization_id: string;
  name: string;
  model?: string;
  paper_width_mm: number;
  paper_height_mm: number;
  orientation: PrinterOrientation;
  is_default: boolean;
  is_active: boolean;
  notes?: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface WhPrinterConfigFormData {
  name: string;
  model?: string;
  paper_width_mm: number;
  paper_height_mm: number;
  orientation?: PrinterOrientation;
  is_default?: boolean;
  notes?: string;
}

export const LABEL_FORMAT_PRESETS: Record<Exclude<LabelFormat, 'custom'>, { width_mm: number; height_mm: number; label: string }> = {
  small: { width_mm: 60, height_mm: 40, label: 'Piccolo (60x40mm)' },
  a5: { width_mm: 148, height_mm: 210, label: 'A5 (148x210mm)' },
  shipping_4x6: { width_mm: 100, height_mm: 150, label: 'Spedizione (100x150mm)' },
};

export interface WhLabelTemplate {
  id: string;
  organization_id: string;
  name: string;
  format: LabelFormat;
  field_config: WhLabelFieldConfig[];
  is_default: boolean;
  is_active: boolean;
  qr_access: QrAccess;
  notes?: string;
  printer_config_id?: string;
  printer_config?: WhPrinterConfig;
  /** NULL = template generico, valido per tutti i prodotti (20260910e). */
  product_id?: string | null;
  product?: { id: string; name: string; sku?: string } | null;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface WhLabelTemplateFormData {
  name: string;
  format: LabelFormat;
  field_config: WhLabelFieldConfig[];
  is_default?: boolean;
  qr_access?: QrAccess;
  notes?: string;
  printer_config_id?: string | null;
  product_id?: string | null;
}

export interface WhLabelPrint {
  id: string;
  organization_id: string;
  template_id: string;
  lot_id: string;
  printed_by?: string;
  printed_at: string;
  copies: number;
  created_at: string;
}

// --- Disposal (Smaltimento) ---

export interface WhDisposal {
  id: string;
  organization_id: string;
  lot_id?: string;
  product_id: string;
  material_type?: string;
  waste_type?: string;
  quantity: number;
  unit: string;
  disposal_date: string;
  document_reference?: string;
  source_type: DisposalSource;
  source_id?: string;
  notes?: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by?: string;

  // Relations
  product?: { id: string; name: string; sku?: string };
  lot?: ProductLot;
}

export interface WhDisposalFormData {
  lot_id?: string;
  product_id: string;
  material_type?: string;
  waste_type?: string;
  quantity: number;
  unit?: string;
  disposal_date: string;
  document_reference?: string;
  source_type: DisposalSource;
  source_id?: string;
  notes?: string;
}

// --- Movement (Movimento unificato) ---

export interface WhMovement {
  id: string;
  organization_id: string;
  movement_type: MovementType;
  product_id: string;
  lot_id?: string;
  zone_from_id?: string;
  zone_to_id?: string;
  quantity: number;
  unit: string;
  reference_type?: string;
  reference_id?: string;
  snapshot_data: Record<string, unknown>;
  performed_by?: string;
  performed_at: string;
  notes?: string;
  created_at: string;

  // Relations (populated by queries)
  product?: { id: string; name: string; sku?: string };
  lot?: { id: string; lot_number: string };
  zone_from?: { id: string; name: string; code: string };
  zone_to?: { id: string; name: string; code: string };
  performer?: { id: string; full_name?: string };
}

// ═══════════════════════════════════════════════════════════════
// Warehouse Sidebar Sections
// ═══════════════════════════════════════════════════════════════

export type WarehouseSection =
  | 'dashboard'
  | 'products'
  | 'bom'
  | 'stock_loading'
  | 'zone_transfers'
  | 'all_movements'
  | 'production_cycles'
  | 'external_processings'
  | 'suppliers'
  | 'contractors'
  | 'inbound_ddts'
  | 'label_templates'
  | 'lot_samples'
  | 'disposals'
  | 'traceability'
  // Existing sections kept
  | 'orders'
  | 'order_preparation'
  | 'woocommerce_sync'
  | 'rentals'
  | 'price_lists'
  | 'analytics'
  | 'zones'
  | 'lots'
  // Area Acquisti
  | 'supplier_orders'
  | 'supplier_price_lists'
  // Area Produzione
  | 'rice_processing'
  | 'quality_controls'
  // Area Gestione
  | 'document_types'
  | 'document_archive'
  | 'consultazioni'
  | 'woo_clients'
  | 'categories'
  | 'attributes'
  | 'price_grid'
  | 'physical_grid'
  // Area Vendita (placeholder)
  | 'outbound_ddts'
  | 'client_orders';

// ═══════════════════════════════════════════════════════════════
// Movement type labels (for UI)
// ═══════════════════════════════════════════════════════════════

export const MOVEMENT_TYPE_LABELS: Record<MovementType, string> = {
  load: 'Carico',
  ddt_out: 'Uscita merce (DDT)',
  production_in: 'Produzione (ingresso)',
  production_out: 'Produzione (consumo)',
  contractor_out: 'Uscita terzista',
  contractor_in: 'Rientro terzista',
  zone_transfer: 'Trasferimento zona',
  sample_taken: 'Prelievo campione',
  sample_returned: 'Restituzione campione',
  disposal: 'Smaltimento',
  waste: 'Scarto',
  waste_sale: 'Vendita scarto',
  label_print: 'Stampa etichetta',
  adjustment: 'Rettifica',
  retransformation_in: 'Ritrasformazione (ingresso)',
  retransformation_out: 'Ritrasformazione (consumo)',
};

export const PRODUCT_CLASS_LABELS: Record<ProductClass, string> = {
  standard: 'Standard',
  materia_prima: 'Materia prima',
  semilavorato: 'Semilavorato',
  referenza: 'Referenza (prodotto finito)',
};

// ═══════════════════════════════════════════════════════════════
// Outbound DDTs (Spedizioni)
// ═══════════════════════════════════════════════════════════════

export type OutboundDdtStatus = 'draft' | 'prepared' | 'shipped' | 'delivered';

export interface WhOutboundDdt {
  id: string;
  organization_id: string;
  client_id?: string;
  ddt_number?: string;
  ddt_date?: string;
  registration_date?: string;
  document_type_id?: string;
  destination?: string;
  destinatario_merce?: string;
  /** Sede/indirizzo di consegna del cliente scelto (client_operational_locations) */
  shipping_location_id?: string | null;
  causale?: string;
  reference?: string;
  warehouse_1_id?: string;
  warehouse_2_id?: string;
  notes?: string;
  status: OutboundDdtStatus;
  total_amount?: number;
  // Flag SALDATO (Wave 6 / D5): valorizzato = documento saldato dal cliente
  paid_at?: string;
  paid_by?: string;
  archived: boolean;
  evaso: boolean;
  snapshot_client_data?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  // Template di stampa DDT di riferimento (wh_ddt_print_templates) — determina
  // quali campi extra configurati mostrare nel form (vedi ddtExtraFields.ts)
  print_template_id?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // Relations
  client?: { id: string; business_name: string };
  document_type?: { id: string; sigla: string; description: string };
  lines?: WhOutboundDdtLine[];
}

export interface WhOutboundDdtLine {
  id: string;
  organization_id: string;
  outbound_ddt_id: string;
  product_id?: string;
  lot_id?: string;
  quantity: number;
  unit: string;
  unit_price?: number;
  detail?: string;
  colli?: number;
  bancali_qty?: number;
  bancali_type?: string;
  unita_singola?: number;
  expiration_date?: string;
  lot_number?: string;
  notes?: string;
  sort_order: number;
  /** true = riga scaricata a stock (movimento ddt_out registrato, Wave 6) */
  unloaded?: boolean;
  /** Ordine cliente da cui arriva la riga («Da ordine», migration 20260916e) */
  order_id?: string | null;
  /** Riga dell'ordine cliente da cui arriva la riga (migration 20260917) */
  order_item_id?: string | null;
  // Valori dei campi extra riga configurati per il template (vedi
  // ddtExtraFields.ts), chiave = DdtExtraFieldDef.key
  extra_fields?: Record<string, unknown>;
  snapshot_product_data?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Relations
  product?: { id: string; name: string; sku?: string };
  lot?: { id: string; lot_number: string; quantity: number; unit: string; expiration_date?: string };
}

export interface WhOutboundDdtFormData {
  registration_date?: string;
  client_id?: string;
  ddt_number?: string;
  ddt_date?: string;
  document_type_id?: string;
  destination?: string;
  destinatario_merce?: string;
  /** Sede/indirizzo di consegna del cliente scelto (client_operational_locations) */
  shipping_location_id?: string | null;
  causale?: string;
  reference?: string;
  warehouse_1_id?: string;
  warehouse_2_id?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  print_template_id?: string | null;
}

export interface WhOutboundDdtLineFormData {
  product_id: string;
  lot_id?: string;
  lot_number?: string;
  quantity: number;
  unit?: string;
  unit_price?: number;
  detail?: string;
  expiration_date?: string;
  colli?: number;
  bancali_qty?: number;
  bancali_type?: string;
  unita_singola?: number;
  notes?: string;
  extra_fields?: Record<string, unknown>;
  /** Ordine cliente da cui arriva la riga («Da ordine») */
  order_id?: string | null;
  /** Riga dell'ordine cliente da cui arriva la riga */
  order_item_id?: string | null;
}

export const OUTBOUND_DDT_STATUS_LABELS: Record<OutboundDdtStatus, string> = {
  draft: 'Bozza',
  prepared: 'Preparato',
  shipped: 'Spedito',
  delivered: 'Consegnato',
};

export const LOT_STATUS_LABELS: Record<LotStatus, string> = {
  available: 'Disponibile',
  reserved: 'Riservato',
  in_production: 'In produzione',
  with_contractor: 'Presso terzista',
  exhausted: 'Esaurito',
  disposed: 'Smaltito',
};

export const DDT_STATUS_LABELS: Record<DdtStatus, string> = {
  draft: 'Bozza',
  received: 'Ricevuta',
  loaded: 'Caricata',
  invoiced: 'Fatturata',
};

export const PRODUCTION_CYCLE_STATUS_LABELS: Record<ProductionCycleStatus, string> = {
  draft: 'Bozza',
  in_progress: 'In corso',
  completed: 'Completato',
  cancelled: 'Annullato',
};

export const EXTERNAL_PROCESSING_STATUS_LABELS: Record<ExternalProcessingStatus, string> = {
  draft: 'Bozza',
  sent: 'Inviato (manuale)',
  in_progress: 'In lavorazione',
  returned: 'Rientrato',
  completed: 'Completato',
  cancelled: 'Annullato',
};

export const SAMPLE_STATUS_LABELS: Record<SampleStatus, string> = {
  active: 'Attivo',
  in_analysis: 'In analisi',
  returned: 'Restituito',
  disposed: 'Smaltito',
  archived: 'Archiviato',
};

// ═══════════════════════════════════════════════════════════════
// Nuovi tipi
// ═══════════════════════════════════════════════════════════════

export type DocumentTypeDirection = 'entry' | 'exit' | 'internal' | 'transformation';

export type SupplierOrderStatus = 'draft' | 'confirmed' | 'partial' | 'fulfilled' | 'cancelled';

export type QualityControlType = 'acceptance' | 'rice' | 'production';

export type QualityControlResult = 'conforme' | 'non_conforme' | 'parziale';

export type BomPhase = 'RIL_CONF' | 'RIL_SCONF' | 'RIL_ETT' | 'RIL_C+E';

// --- Document Type ---

export interface WhDocumentType {
  id: string;
  organization_id: string;
  sigla: string;
  description: string;
  direction: DocumentTypeDirection;
  causale?: string;
  settings: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface WhDocumentTypeFormData {
  sigla: string;
  description: string;
  direction: DocumentTypeDirection;
  causale?: string;
  settings?: Record<string, unknown>;
  is_active?: boolean;
}

// --- Supplier Order ---

export interface WhSupplierOrder {
  id: string;
  organization_id: string;
  supplier_id: string;
  document_type_id?: string;
  order_number?: string;
  order_seq?: number;
  order_year?: number;
  order_date: string;
  expected_delivery_date?: string;
  status: SupplierOrderStatus;
  notes?: string;
  subject?: string;
  internal_subject?: string;
  language?: string;
  currency?: string;
  payment_method?: string;
  payment_details?: string;
  total: number;
  archived: boolean;
  snapshot_supplier_data: Record<string, unknown>;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // Relations
  supplier?: WhSupplier;
  lines?: WhSupplierOrderLine[];
}

export interface WhSupplierOrderLine {
  id: string;
  organization_id: string;
  order_id: string;
  product_id: string;
  quantity: number;
  unit: string;
  unit_price?: number;
  vat_rate?: number;
  lot_number?: string;
  expiry_date?: string;
  notes?: string;
  fulfilled_quantity: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
  // Relations
  product?: { id: string; name: string; sku?: string; unit?: string };
}

export interface WhSupplierOrderFormData {
  supplier_id: string;
  document_type_id?: string;
  order_number?: string;
  order_date: string;
  expected_delivery_date?: string;
  notes?: string;
  subject?: string;
  internal_subject?: string;
  language?: string;
  currency?: string;
  payment_method?: string;
  payment_details?: string;
}

export interface WhSupplierOrderLineFormData {
  product_id: string;
  quantity: number;
  unit: string;
  unit_price?: number;
  vat_rate?: number;
  lot_number?: string;
  expiry_date?: string;
  notes?: string;
}

// --- Supplier Price List ---

export interface WhSupplierPriceList {
  id: string;
  organization_id: string;
  supplier_id: string;
  name: string;
  valid_from?: string;
  valid_to?: string;
  is_active: boolean;
  file_url?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Relations
  supplier?: WhSupplier;
  items?: WhSupplierPriceListItem[];
}

export interface WhSupplierPriceListItem {
  id: string;
  organization_id: string;
  price_list_id: string;
  product_id: string;
  unit_price?: number;
  min_quantity?: number;
  notes?: string;
  created_at: string;
  // Relations
  product?: { id: string; name: string; sku?: string; unit_of_measure_1?: string | null };
}

// --- Quality Control ---

export interface WhQualityControl {
  id: string;
  organization_id: string;
  control_type: QualityControlType;
  reference_type: string;
  reference_id: string;
  lot_id?: string;
  checklist_data: Record<string, unknown>;
  result?: QualityControlResult;
  operator_name?: string;
  operator_signature?: string;
  quality_operator_name?: string;
  quality_signature?: string;
  quality_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// --- Rice Processing Record ---

export interface WhRiceProcessingRecord {
  id: string;
  organization_id: string;
  crop_year: string;
  ber_number?: number;
  ber_date?: string;
  causale: string;
  destination?: string;
  variety: string;
  kg_net_paddy: number;
  kg_processed?: number;
  yield_percentage?: number;
  processing_cost_per_quintal?: number;
  total_processing_cost?: number;
  real_cost_per_quintal?: number;
  breakage_sale_amount?: number;
  lot_number?: string;
  lot_id?: string;
  external_processing_id?: string;
  notes?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface WhRiceProcessingFormData {
  crop_year: string;
  ber_number?: number;
  ber_date?: string;
  causale?: string;
  destination?: string;
  variety: string;
  kg_net_paddy: number;
  kg_processed?: number;
  processing_cost_per_quintal?: number;
  total_processing_cost?: number;
  real_cost_per_quintal?: number;
  breakage_sale_amount?: number;
  lot_number?: string;
  notes?: string;
}

// --- Document Archive ---

export interface WhDocumentArchiveItem {
  id: string;
  organization_id: string;
  parent_type: string;
  parent_id: string;
  file_url: string;
  file_name?: string;
  file_type?: string;
  description?: string;
  metadata: Record<string, unknown>;
  uploaded_by?: string;
  created_at: string;
}

// --- Labels ---

export const SUPPLIER_ORDER_STATUS_LABELS: Record<SupplierOrderStatus, string> = {
  draft: 'Bozza',
  confirmed: 'Confermato',
  partial: 'Parziale',
  fulfilled: 'Evaso',
  cancelled: 'Annullato',
};

export const DOCUMENT_TYPE_DIRECTION_LABELS: Record<DocumentTypeDirection, string> = {
  entry: 'Entrata',
  exit: 'Uscita',
  internal: 'Interno',
  transformation: 'Trasformazione',
};

export const DOCUMENT_TYPE_DIRECTION_SHORT: Record<DocumentTypeDirection, string> = {
  entry: 'Entr.',
  exit: 'Usc.',
  internal: 'Int.',
  transformation: 'Trasf.',
};

export const QUALITY_CONTROL_TYPE_LABELS: Record<QualityControlType, string> = {
  acceptance: 'Accettazione',
  rice: 'Controllo Riso',
  production: 'Produzione',
};

export const QUALITY_CONTROL_RESULT_LABELS: Record<QualityControlResult, string> = {
  conforme: 'Conforme',
  non_conforme: 'Non Conforme',
  parziale: 'Parziale',
};

export const BOM_PHASE_LABELS: Record<BomPhase, string> = {
  'RIL_CONF': 'Confezionamento',
  'RIL_SCONF': 'Sconfezionamento',
  'RIL_ETT': 'Etichettatura',
  'RIL_C+E': 'Confez. + Etichettatura',
};

// ═══════════════════════════════════════════════════════════════
// Traceability Graph Types
// ═══════════════════════════════════════════════════════════════

export interface TraceGraphNode {
  documents?: Array<{ type: string; id: string; label?: string }>;
  id: string;
  lot_id: string;
  lot_number: string;
  product_name: string;
  product_sku?: string;
  product_id: string;
  quantity: number;
  unit: string;
  expiration_date?: string;
  created_at: string;
  status?: LotStatus;
  supplier_name?: string;
  client_name?: string;
  ddt_number?: string;
  ddt_date?: string;
  origin_type?: string;
  depth: number;
  isRoot: boolean;
}

export interface TraceGraphEdge {
  from: string;
  to: string;
  type: 'production' | 'processing' | 'ddt_out';
}

export interface TraceGraph {
  nodes: TraceGraphNode[];
  edges: TraceGraphEdge[];
  rootLotId: string;
  mode: 'forward' | 'backward';
}
